// Shown after every Enrich/Regenerate Description/Re-run Research run —
// nothing from that run is written to HubSpot until the user reviews it
// here and confirms, even when every proposed value is a plain fill-in-the-
// blank with nothing existing to overwrite (an "addition") rather than an
// actual conflict with existing content. With zero proposed changes this
// still opens, just as a "nothing to update" confirmation.

import React from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const PropertyEnrichReviewDialog = ({ open, changes, infoMessages, selections, onSelectionChange, onCancel, onConfirm, onRetry, saving, error }) => {
	const list = changes || [];
	const hasChanges = list.length > 0;
	const messages = infoMessages || [];
	// "Everything already matches" is only true when the run actually
	// succeeded and genuinely found nothing different — if it came back with
	// warnings (e.g. description generation couldn't ground a result), there
	// was nothing to compare in the first place, so claiming a match would
	// be misleading. The notice below already explains that case on its own.
	const cleanNoOp = !hasChanges && !messages.length;

	return (
		<Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="md" fullWidth>
			<DialogTitle className="propertyViewerEditDialogTitle">Review Enrichment Changes</DialogTitle>
			{/* propertyViewerEnrichReviewContent stops this DialogContent from
			    scrolling itself (MUI's default) — the header below stays fixed
			    while propertyViewerEnrichReviewList becomes its own independent
			    scroll region, the standard reliable pattern for "fixed header
			    over a scrollable list" instead of relying on position: sticky
			    inside a directly-scrolling flex column. */}
			<DialogContent className="propertyViewerEditDialogContent propertyViewerEnrichReviewContent">
				{messages.length > 0 && (
					<div className="propertyViewerEnrichNotice">
						<div className="propertyViewerEnrichNoticeHeader">
							<WarningAmberIcon fontSize="small" />
							<Typography variant="subtitle2">Nothing to review</Typography>
						</div>
						{messages.map((message, i) =>
							i === 0 ? (
								<Typography key={i} variant="body2">
									{message}
								</Typography>
							) : (
								<div key={i} className="propertyViewerEnrichNoticeDetail">
									{message}
								</div>
							),
						)}
					</div>
				)}

				{hasChanges ? (
					<>
						<div className="propertyViewerEnrichReviewHeader">
							<Alert severity="info" sx={{ mb: 2 }}>
								Nothing has been saved to HubSpot yet. Pick which value to keep for each field below, then confirm.
							</Alert>
							<div className="propertyViewerEnrichColumnHeaders">
								<span>Existing</span>
								<span>New</span>
							</div>
						</div>
						<div className="propertyViewerEnrichReviewList">
							{list.map((c, i) => (
								<React.Fragment key={c.key}>
									{i > 0 && <Divider sx={{ my: 1.5 }} />}
									<div className="propertyViewerEnrichDiffRow">
										<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
											{c.label}
										</Typography>
										<ToggleButtonGroup
											exclusive
											size="small"
											color="primary"
											fullWidth
											value={selections[c.key] || "new"}
											onChange={(e, val) => { if (val) onSelectionChange(c.key, val); }}
										>
											<ToggleButton value="old" sx={{ textTransform: "none", whiteSpace: "normal", textAlign: "left" }}>
												{c.status === "addition" ? "Skip" : c.oldValue || "—"}
											</ToggleButton>
											<ToggleButton value="new" sx={{ textTransform: "none", whiteSpace: "normal", textAlign: "left" }}>
												{c.newValue || "—"}
											</ToggleButton>
										</ToggleButtonGroup>
									</div>
								</React.Fragment>
							))}
						</div>
					</>
				) : cleanNoOp ? (
					<Typography variant="body2" color="text.secondary">
						No changes. Everything Claude found already matches what's in HubSpot.
					</Typography>
				) : null}

				{error && (
					<Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
						{error}
					</Typography>
				)}
				<div className="dialogButtons">
					{hasChanges ? (
						<>
							<Button variant="outlined" color="primary" onClick={onCancel} disabled={saving}>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								disableElevation
								onClick={onConfirm}
								disabled={saving}
								startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
							>
								{saving ? "Saving…" : "Save Selections"}
							</Button>
						</>
					) : (
						<>
							{messages.length > 0 && (
								<Button variant="outlined" color="primary" onClick={onRetry}>
									Try Again
								</Button>
							)}
							<Button variant="contained" color="primary" disableElevation onClick={onCancel}>
								Close
							</Button>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PropertyEnrichReviewDialog;
